'use client';

import Link from 'next/link';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { InlineLanguageSwitcher } from '@/components/InlineLanguageSwitcher/InlineLanguageSwitcher';
import { ThemeSwitcher } from '@/components/ThemeSwitcher/ThemeSwitcher';
import { SocialLinks } from '@/components/ui/SocialLinks/SocialLinks';
import { CONFIG } from '@/config';
import { PROFILE_MODES } from '@/constants/common';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useStaticPages } from '@/hooks/useStaticPages';
import { useProfileMode } from '@/providers/ProfileModeProvider/ProfileModeProvider';

import * as S from './TapBar.styles';

const {
  contacts: { email, emailUrl, tel, telUrl }
} = CONFIG;

interface TapBarProps {
  isOpen: boolean;
}

export const TapBarMenu: FC<TapBarProps> = ({ isOpen }) => {
  const { t } = useTranslation();
  const { pages } = useStaticPages();
  useBodyScrollLock(isOpen);

  const { profileMode, switchProfileMode, canSwitchProfileMode } =
    useProfileMode();

  return (
    <S.MenuContent $isOpen={isOpen}>
      <div>
        <S.MenuTitle variant="h1" htmlTag="h2">
          {t('BurgerMenu.title')}
        </S.MenuTitle>
        <S.MenuLinksWrapper>
          {pages.map(page => (
            <S.MenuLink
              key={page.slug}
              as={Link}
              href={page.href}
              prefetch={false}
            >
              {page.title}
            </S.MenuLink>
          ))}
          {canSwitchProfileMode && (
            <S.MenuLink
              onClick={switchProfileMode}
              data-cy="tapbar-menu-switch-profile-mode-button"
            >
              {profileMode === PROFILE_MODES.COMPANY
                ? t('ProfileMenu.switch_on_candidate_profile_label')
                : t('ProfileMenu.switch_on_company_profile_label')}
            </S.MenuLink>
          )}
        </S.MenuLinksWrapper>

        <S.ThemeSwitcherWrapper>
          <ThemeSwitcher />
        </S.ThemeSwitcherWrapper>
      </div>

      <S.ContactsAndLanguageWrapper>
        <S.ContactsTitle>{t('BurgerMenu.contacts_title')}</S.ContactsTitle>

        <S.ContactsItem as={Link} href={telUrl}>
          {tel}
        </S.ContactsItem>
        <S.ContactsItem as={Link} href={emailUrl}>
          {email}
        </S.ContactsItem>

        <SocialLinks gap={30} />

        <InlineLanguageSwitcher />
      </S.ContactsAndLanguageWrapper>
    </S.MenuContent>
  );
};
